/**
 * /kiosk/admin/seed — owner-only maintenance.
 *
 * Creates the /barbers records the booking wizard reads. Without them
 * resolveWorkingWindow finds no weeklyHours, every barber resolves to
 * "not working", and getFreeBusy returns zero slots for everyone.
 *
 * Gated on the OWNER's kiosk PIN — the same credential they already use to
 * clock in, already bcrypt-hashed with lockout. A second admin secret would
 * be one more thing nobody rotates.
 *
 * Staff-facing and English-only on purpose: this is an internal maintenance
 * screen, not a customer surface, so it stays out of the copy dictionaries.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFns } from '../../lib/firebase';
import { useShopConfig } from '../../hooks/useShopConfig';

interface SeedResult {
  seeded: number;
  created: number;
  updated: number;
  at: number;
}

interface ResetResult {
  reset: boolean;
  barberSlug: string;
  at: number;
}

/** Both actions share one owner PIN, so they share one error mapping. */
function ownerActionError(err: unknown, ownerName: string): string {
  const e = err as { code?: string };
  const code = e.code ?? '';
  if (code.includes('permission-denied')) return 'That PIN did not match.';
  if (code.includes('resource-exhausted')) return 'Too many attempts. Try again in 10 minutes.';
  if (code.includes('failed-precondition'))
    return `${ownerName} has no PIN set yet. Set one at /staff first.`;
  return 'That did not go through. Try again.';
}

export default function AdminSeed() {
  const { barbers, config } = useShopConfig();
  const owner = barbers.find((b) => 'is_owner' in b && b.is_owner);

  const ownerName = owner?.display_name ?? 'The owner';

  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SeedResult | null>(null);

  const [resetSlug, setResetSlug] = useState(barbers[0]?.slug ?? '');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState<ResetResult | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const fn = httpsCallable<{ pin: string }, SeedResult>(getFns(), 'seedBarbers');
      const res = await fn({ pin });
      setResult(res.data);
      setPin('');
    } catch (err) {
      setError(ownerActionError(err, ownerName));
      console.error('seedBarbers failed:', err);
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const resetPin = async () => {
    setResetBusy(true);
    setResetError(null);
    setResetDone(null);
    try {
      const fn = httpsCallable<{ barberSlug: string; ownerPin: string }, ResetResult>(
        getFns(),
        'resetBarberPin'
      );
      const res = await fn({ barberSlug: resetSlug, ownerPin: pin });
      setResetDone(res.data);
      setPin('');
    } catch (err) {
      setResetError(ownerActionError(err, ownerName));
      console.error('resetBarberPin failed:', err);
      setPin('');
    } finally {
      setResetBusy(false);
    }
  };

  const resetTargetName =
    barbers.find((b) => b.slug === resetDone?.barberSlug)?.display_name ?? '';

  return (
    <div className="kd-kiosk-root min-h-screen bg-kiosk-black text-oncrest-text p-10">
      <h1 className="font-serif text-3xl">Barber records</h1>
      <p className="text-oncrest-soft mt-3 max-w-xl">
        Creates the {barbers.length} barber records the booking system reads. Safe to
        run more than once — PINs, iCal URLs and adjusted hours are left alone.
      </p>

      <div className="max-w-sm mt-10">
        <label className="block">
          <span className="kd-kicker text-gold text-xs uppercase tracking-wide">
            {owner?.display_name ?? 'Owner'}&apos;s PIN
          </span>
          <input
            inputMode="numeric"
            maxLength={config.pin_length}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            className="mt-2 w-full text-center tracking-[0.5em] text-3xl bg-navy-raised rounded-tile py-4"
            aria-label="Owner PIN"
          />
        </label>

        {error && (
          <p role="alert" className="text-red-accent mt-4">
            {error}
          </p>
        )}

        {result && (
          <p role="status" className="text-oncrest-text mt-4">
            Seeded {result.seeded} barbers.{' '}
            <span className="text-oncrest-soft">
              {result.created} created, {result.updated} updated ·{' '}
              {new Date(result.at).toLocaleTimeString()}
            </span>
          </p>
        )}

        <button
          type="button"
          onClick={run}
          disabled={busy || resetBusy || pin.length < config.pin_length}
          className="mt-8 w-full bg-gold text-navy rounded-pill py-4 font-serif text-xl disabled:opacity-50"
        >
          {busy ? '…' : 'Seed barber records'}
        </button>

        {/* Same PIN field gates both actions — one credential, entered once. */}
        <hr className="border-navy-border my-10" />

        <h2 className="font-serif text-2xl">Reset a barber&apos;s PIN</h2>
        <p className="text-oncrest-soft mt-2 text-sm">
          Clears their PIN. The next tap on their photo prompts them to set a new one.
        </p>

        <label className="block mt-6">
          <span className="kd-kicker text-gold text-xs uppercase tracking-wide">Barber</span>
          <select
            value={resetSlug}
            onChange={(e) => setResetSlug(e.target.value)}
            className="mt-2 w-full bg-navy-raised rounded-tile px-4 py-3 text-xl font-serif"
          >
            {barbers.map((b) => (
              <option key={b.slug} value={b.slug}>
                {b.display_name}
              </option>
            ))}
          </select>
        </label>

        {resetError && (
          <p role="alert" className="text-red-accent mt-4">
            {resetError}
          </p>
        )}

        {resetDone && (
          <p role="status" className="text-oncrest-text mt-4">
            {resetTargetName}&apos;s PIN cleared. Next tap on their photo will prompt
            Set your PIN.{' '}
            <span className="text-oncrest-soft">
              {new Date(resetDone.at).toLocaleTimeString()}
            </span>
          </p>
        )}

        <button
          type="button"
          onClick={resetPin}
          disabled={busy || resetBusy || pin.length < config.pin_length || !resetSlug}
          className="mt-6 w-full border-2 border-red-accent text-oncrest-text rounded-pill py-4 font-serif text-xl disabled:opacity-50"
        >
          {resetBusy ? '…' : 'Reset PIN'}
        </button>
      </div>
    </div>
  );
}
